import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  IconButton, Alert, CircularProgress, Chip, TextField,
  MenuItem, Divider, Collapse, InputAdornment, Tooltip,
  useTheme, alpha
} from '@mui/material';
import {
  Add, Visibility, Delete, Edit, FilterList, Clear,
  TrendingUp, Inventory2, ExpandMore, ExpandLess, Search
} from '@mui/icons-material';
import { Offer } from '../../types';
import { getOffers, deleteOffer } from '../../api/offers';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

// ─── Sabitler ─────────────────────────────────────────────────────────────────
const BRAND  = '#1A3A6B';
const ACCENT = '#c0392b';
const LME_AL_PRIM = 3530;

const statusLabel: Record<string, string> = {
  DRAFT: 'Taslak', SENT: 'Gönderildi', ACCEPTED: 'Kabul', REJECTED: 'Red'
};
const statusStyle: Record<string, { bg: string; color: string; darkBg: string }> = {
  DRAFT:    { bg: '#fff7ed', color: '#c2410c', darkBg: 'rgba(194,65,12,.2)'  },
  SENT:     { bg: '#eff6ff', color: '#1d4ed8', darkBg: 'rgba(29,78,216,.2)'  },
  ACCEPTED: { bg: '#f0fdf4', color: '#15803d', darkBg: 'rgba(21,128,61,.2)'  },
  REJECTED: { bg: '#fff0f0', color: '#dc2626', darkBg: 'rgba(220,38,38,.2)'  },
};

// MUI color prop'u artık kullanmıyoruz — tamamen sx ile
const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const isDark = useTheme().palette.mode === 'dark';
  const s = statusStyle[status] || { bg: '#f4f5f7', color: '#6b7280', darkBg: 'rgba(107,114,128,.2)' };
  return (
      <Box component="span" sx={{
        display: 'inline-block', px: 1, py: '3px',
        borderRadius: '20px', fontSize: '11px', fontWeight: 700, lineHeight: 1.4,
        bgcolor: isDark ? s.darkBg : s.bg,
        color: s.color,
      }}>
        {statusLabel[status] || status}
      </Box>
  );
};
const matColor: Record<string, string> = {
  AL: '#fff3e0', XLPE: '#e3f2fd', PVC: '#fce4ec',
  PE: '#f3e5f5', HFFR: '#e8f5e9', HDPE: '#e0f7fa', CELIK: '#eceff1'
};
const matLabel: Record<string, string> = { CELIK: 'Çelik' };

const fmt = (n: number, decimals = 2) =>
    n.toLocaleString('tr-TR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

// ─── Interfaces ───────────────────────────────────────────────────────────────
interface Summary {
  offerCount: number; totalAmount: number; totalCost: number;
  totalKg: number; alCost: number; alKg: number;
  digerCost: number; brutKarlilik: number; netKarlilik: number;
}

// ─── Yardımcı fonksiyon ───────────────────────────────────────────────────────
const calcOfferSummary = (offer: Offer) => {
  if (!offer.items || offer.items.length === 0) return null;
  const currency = offer.currency || 'USD';
  let totalAmount = 0, totalCost = 0, totalKg = 0;
  let alCost = 0, alKg = 0, totalIsletme = 0;
  const hammedde: Record<string, { cost: number; kg: number }> = {};

  offer.items.forEach(item => {
    const qty    = item.quantity || 0;
    const total  = item.totalPrice || 0;
    const cost   = (item.costPriceSnapshot ?? item.costPrice ?? 0) * qty;
    const weight = (item.unitWeight || 0) * qty;
    const alFactor = item.alFactorSnapshot ?? item.aluminumFactor ?? 0;
    const lme    = (item.lmeSnapshot && item.lmeSnapshot > 0) ? item.lmeSnapshot : LME_AL_PRIM;
    totalAmount += total; totalCost += cost; totalKg += weight;

    let alCostItem: number;
    let izolasyonCost = 0, dolguCost = 0, kilafCost = 0, isletmeCost = 0;
    if (item.alMaliyetSnapshot != null) {
      alCostItem    = (item.alMaliyetSnapshot || 0) * qty;
      izolasyonCost = (item.izolasyonMaliyetSnapshot || 0) * qty;
      dolguCost     = (item.dolguMaliyetSnapshot || 0) * qty;
      kilafCost     = (item.kilafMaliyetSnapshot || 0) * qty;
      isletmeCost   = (item.isletmeMaliyetSnapshot || 0) * qty;
    } else {
      const onlySafAl = ['Compacted', 'AAC-ACSR'].includes(item.cableFamily || '');
      alCostItem = onlySafAl ? cost : alFactor * qty * lme * 0.001;
    }
    alCost += alCostItem; alKg += alFactor * qty; totalIsletme += isletmeCost;

    const family = item.cableFamily || '';
    const izKey  = family === 'AAC-ACSR' ? 'CELIK'
        : family === 'NA2XY' || family === 'NA2XH' || family === 'ABC XLPE' ? 'XLPE'
        : family === 'AMKA PE' || family === 'AER' ? 'PE' : 'PVC';
    const kilafKey = family === 'NA2XH' ? 'HFFR'
        : family === 'ABC HDPE' ? 'HDPE'
            : family === 'ABC XLPE' || family === 'AMKA PE' || family === 'AER' ? 'PE' : 'PVC';
    const dolguKey = family === 'NA2XH' ? 'HFFR' : 'PE';

    if (izolasyonCost > 0.01) { if (!hammedde[izKey])    hammedde[izKey]    = { cost: 0, kg: 0 }; hammedde[izKey].cost += izolasyonCost; }
    if (dolguCost     > 0.01) { if (!hammedde[dolguKey]) hammedde[dolguKey] = { cost: 0, kg: 0 }; hammedde[dolguKey].cost += dolguCost; }
    if (kilafCost     > 0.01) { if (!hammedde[kilafKey]) hammedde[kilafKey] = { cost: 0, kg: 0 }; hammedde[kilafKey].cost += kilafCost; }
  });

  const usdTry = offer.usdTrySnapshot || 1;
  const eurTry = offer.eurTrySnapshot || 1;
  const toOfferCurrency = (usd: number) => {
    if (currency === 'TRY') return usd * usdTry;
    if (currency === 'EUR') return usd / (eurTry / usdTry);
    return usd;
  };
  const hammaddeToplam = toOfferCurrency(alCost + Object.values(hammedde).reduce((s, v) => s + v.cost, 0));
  const isletmeToplam  = toOfferCurrency(totalIsletme);
  Object.keys(hammedde).forEach(k => { hammedde[k].cost = toOfferCurrency(hammedde[k].cost); });
  const alCostConverted = toOfferCurrency(alCost);
  const brutKar = totalAmount > 0 ? (totalAmount - hammaddeToplam) / totalAmount * 100 : 0;
  const netKar  = totalAmount > 0 ? (totalAmount - hammaddeToplam - isletmeToplam) / totalAmount * 100 : 0;
  return { totalAmount, totalCost, hammaddeToplam, totalKg, alCost: alCostConverted, alKg, totalIsletme: isletmeToplam, hammedde, brutKar, netKar, currency };
};

// ─── MetricCard ───────────────────────────────────────────────────────────────
const MetricCard: React.FC<{ label: string; val: string; bg: string; color: string }> = ({ label, val, bg, color }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
      <Box sx={{
        p: 1.5, flex: '1 1 120px', minWidth: 120,
        bgcolor: isDark ? alpha(color, 0.1) : bg,
        borderRadius: '8px',
      }}>
        <Typography sx={{ fontSize: '10px', color: theme.palette.text.secondary, mb: 0.4, lineHeight: 1.3 }}>{label}</Typography>
        <Typography sx={{ fontWeight: 700, fontSize: '14px', color: isDark ? alpha(color, 0.9) : color, lineHeight: 1.3 }}>{val}</Typography>
      </Box>
  );
};

// ─── OffersPage ───────────────────────────────────────────────────────────────
const OffersPage: React.FC = () => {
  const theme   = useTheme();
  const isDark  = theme.palette.mode === 'dark';
  const BORDER  = theme.palette.divider;
  const SURFACE = theme.palette.background.paper;
  const TEXT2   = theme.palette.text.secondary;

  const [offers, setOffers]       = useState<Offer[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [summary, setSummary]     = useState<Summary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [search, setSearch]       = useState('');
  const { isAdmin }               = useAuth();
  const navigate                  = useNavigate();

  const [searchParams] = useSearchParams();
  const [filterUser,     setFilterUser]     = useState(searchParams.get('user')     || '');
  const [filterStatus,   setFilterStatus]   = useState(searchParams.get('status')   || '');
  const [filterCustomer, setFilterCustomer] = useState(searchParams.get('customer') || '');
  const [filterType,     setFilterType]     = useState('');

  const load = async () => {
    try { setLoading(true); setOffers(await getOffers(isAdmin)); }
    catch { setError('Teklifler yüklenemedi'); }
    finally { setLoading(false); }
  };

  const loadSummary = async (user: string, status: string, customer: string) => {
    setSummaryLoading(true);
    try {
      const params = new URLSearchParams();
      if (user)     params.set('user', user);
      if (status)   params.set('status', status);
      if (customer) params.set('customer', customer);
      const res = await api.get(`/offers/summary?${params.toString()}`);
      setSummary(res.data);
    } catch { setSummary(null); }
    finally { setSummaryLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (filterUser || filterStatus || filterCustomer) loadSummary(filterUser, filterStatus, filterCustomer);
    else setSummary(null);
  }, [filterUser, filterStatus, filterCustomer]);

  const users     = useMemo(() => offers.map(o => o.createdByName).filter(Boolean).filter((v, i, a) => a.indexOf(v) === i), [offers]);
  const customers = useMemo(() => offers.map(o => o.customerName).filter(Boolean).filter((v, i, a) => a.indexOf(v) === i),  [offers]);

  const filtered = useMemo(() => offers.filter(o => {
    const typeOk = !filterType ||
        (filterType === 'FL'     && o.orderNo.includes('-FL-')) ||
        (filterType === 'NORMAL' && !o.orderNo.includes('-FL-'));
    return (!filterUser     || o.createdByName === filterUser)   &&
        (!filterStatus   || o.status === filterStatus)         &&
        (!filterCustomer || o.customerName === filterCustomer) &&
        typeOk &&
        (!search || o.orderNo.toLowerCase().includes(search.toLowerCase()) ||
            o.customerName?.toLowerCase().includes(search.toLowerCase()));
  }), [offers, filterUser, filterStatus, filterCustomer, filterType, search]);

  const hasFilter = !!(filterUser || filterStatus || filterCustomer || filterType);

  const clearFilters = () => {
    setFilterUser(''); setFilterStatus(''); setFilterCustomer('');
    setFilterType(''); setSearch('');
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Teklifi silmek istediğinize emin misiniz?')) return;
    try { await deleteOffer(id); load(); }
    catch { setError('Silme sırasında hata oluştu'); }
  };

  // Tablo kolon sayısı
  const colSpan = isAdmin ? 9 : 8;

  return (
      <Box sx={{ p: 3, bgcolor: theme.palette.background.default, minHeight: '100vh' }}>

        {/* ── Header ── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
              Teklifler
            </Typography>
            <Typography sx={{ fontSize: '13px', color: TEXT2, mt: 0.5 }}>
              <Box component="span" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>{filtered.length}</Box> teklif
              {filtered.length !== offers.length && (
                  <Box component="span" sx={{ color: TEXT2 }}> / {offers.length} toplam</Box>
              )}
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />}
                  onClick={() => navigate('/offers/new')}
                  sx={{
                    bgcolor: ACCENT, fontWeight: 600, borderRadius: '8px',
                    px: 2.5, fontSize: '13px', boxShadow: 'none', height: 38,
                    '&:hover': { bgcolor: '#a93226', boxShadow: 'none' },
                  }}>
            Yeni Teklif
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* ── Filtre paneli ── */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: SURFACE }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: TEXT2, flexShrink: 0 }}>
              <FilterList sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Filtre</Typography>
            </Box>

            <TextField
                placeholder="Teklif no veya müşteri..." value={search}
                onChange={e => setSearch(e.target.value)} size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: TEXT2 }} /></InputAdornment>,
                  sx: { borderRadius: '8px', fontSize: '13px', minWidth: 220 },
                }}
                sx={{ '& fieldset': { borderColor: BORDER } }}
            />

            {isAdmin && (
                <TextField select label="Hazırlayan" value={filterUser}
                           onChange={e => setFilterUser(e.target.value)} size="small"
                           sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                  <MenuItem value="">Tümü</MenuItem>
                  {users.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                </TextField>
            )}
            <TextField select label="Müşteri" value={filterCustomer}
                       onChange={e => setFilterCustomer(e.target.value)} size="small"
                       sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
              <MenuItem value="">Tümü</MenuItem>
              {customers.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField select label="Durum" value={filterStatus}
                       onChange={e => setFilterStatus(e.target.value)} size="small"
                       sx={{ minWidth: 130, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
              <MenuItem value="">Tümü</MenuItem>
              {['DRAFT','SENT','ACCEPTED','REJECTED'].map(s => (
                  <MenuItem key={s} value={s}>{statusLabel[s]}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Tip" value={filterType}
                       onChange={e => setFilterType(e.target.value)} size="small"
                       sx={{ minWidth: 130, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
              <MenuItem value="">Tümü</MenuItem>
              <MenuItem value="NORMAL">LME Bazlı</MenuItem>
              <MenuItem value="FL">Fiyat Listesi</MenuItem>
            </TextField>

            {(hasFilter || search) && (
                <Button size="small" startIcon={<Clear sx={{ fontSize: 14 }} />} onClick={clearFilters}
                        sx={{ ml: 'auto', color: TEXT2, textTransform: 'none', fontSize: '12px' }}>
                  Temizle
                </Button>
            )}
          </Box>

          {/* Aktif filtre etiketleri */}
          {hasFilter && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5, pt: 1.5, borderTop: `1px solid ${BORDER}` }}>
                {filterUser     && <Chip label={filterUser}              size="small" onDelete={() => setFilterUser('')}     sx={{ bgcolor: '#0A1628', color: '#fff', height: 22, fontSize: '11px' }} />}
                {filterCustomer && <Chip label={filterCustomer}          size="small" onDelete={() => setFilterCustomer('')} sx={{ bgcolor: BRAND,    color: '#fff', height: 22, fontSize: '11px' }} />}
                {filterStatus   && <Chip label={statusLabel[filterStatus]} size="small" onDelete={() => setFilterStatus('')} sx={{ height: 22, fontSize: '11px', bgcolor: statusStyle[filterStatus]?.bg, color: statusStyle[filterStatus]?.color }} />}
                {filterType     && <Chip label={filterType === 'FL' ? 'Fiyat Listesi' : 'LME Bazlı'} size="small" onDelete={() => setFilterType('')} sx={{ bgcolor: filterType === 'FL' ? '#7C3AED' : BRAND, color: '#fff', height: 22, fontSize: '11px' }} />}
              </Box>
          )}
        </Paper>

        {/* ── Özet paneli (admin + filtre aktifse) ── */}
        {isAdmin && (filterUser || filterStatus || filterCustomer) && (
            <Paper elevation={0} sx={{
              p: 2.5, mb: 2, borderRadius: '10px',
              border: `1px solid ${isDark ? 'rgba(91,143,232,.2)' : '#dbeafe'}`,
              bgcolor: isDark ? alpha('#1A3A6B', 0.15) : '#f8faff',
            }}>
              {summaryLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
              ) : summary ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <TrendingUp sx={{ color: BRAND, fontSize: 16 }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '12px', color: BRAND, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        Teklif Analizi — {summary.offerCount} teklif
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                      <MetricCard label="Toplam Tutar"     val={`$${fmt(summary.totalAmount)}`}     bg="#e3f2fd" color="#1565c0" />
                      <MetricCard label="Toplam Hammadde"  val={`$${fmt(summary.totalCost)}`}       bg="#f3e5f5" color="#6a1b9a" />
                      <MetricCard label="Brüt Kârlılık"    val={`%${fmt(summary.brutKarlilik, 1)}`} bg="#e8f5e9" color="#2e7d32" />
                      <MetricCard label="Net Kârlılık"     val={`%${fmt(summary.netKarlilik, 1)}`}  bg="#e8f5e9" color="#2e7d32" />
                      <MetricCard label="Toplam Ağırlık"   val={`${fmt(summary.totalKg, 0)} kg`}    bg="#fff8e1" color="#e65100" />
                    </Box>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                      <Inventory2 sx={{ fontSize: 13, color: TEXT2 }} />
                      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: TEXT2, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                        Hammadde Dağılımı
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <MetricCard label="Alüminyum"       val={`$${fmt(summary.alCost)}`}   bg={matColor['AL']} color="#e65100" />
                      <MetricCard label="Diğer Hammadde"  val={`$${fmt(summary.digerCost)}`} bg="#fce4ec"        color="#880e4f" />
                      <MetricCard label="Brüt Kâr"        val={`$${fmt(summary.totalAmount - summary.totalCost)}`} bg="#f0fdf4" color="#166534" />
                    </Box>
                  </>
              ) : null}
            </Paper>
        )}

        {/* ── Tablo ── */}
        {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <CircularProgress sx={{ color: BRAND }} />
            </Box>
        ) : (
            <TableContainer component={Paper} elevation={0}
                            sx={{ borderRadius: '10px', border: `1px solid ${BORDER}`, overflow: 'hidden', bgcolor: SURFACE }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ background: '#f8f9fc', width: 40, paddingTop: 12, paddingBottom: 12, borderBottom: '1px solid #e8eaf0', color: '#8a92a6' }} />
                    {['Teklif No','Müşteri', ...(isAdmin ? ['Hazırlayan'] : []), 'Para Birimi','Teslim','Durum','Tarih','İşlem'].map(h => (
                        <TableCell key={h} align={h === 'İşlem' ? 'center' : 'left'}
                                   style={{ background: '#f8f9fc', paddingTop: 12, paddingBottom: 12,
                                     fontSize: 11, fontWeight: 600, color: '#8a92a6',
                                     textTransform: 'uppercase', letterSpacing: '0.05em',
                                     borderBottom: '1px solid #e8eaf0' }}>
                          {h}
                        </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={colSpan} align="center" sx={{ py: 8, color: TEXT2 }}>
                          <Typography sx={{ mb: 1, fontSize: '14px' }}>
                            {hasFilter || search ? 'Filtreyle eşleşen teklif bulunamadı' : 'Henüz teklif oluşturulmamış'}
                          </Typography>
                          {!hasFilter && !search && (
                              <Button variant="outlined" size="small" startIcon={<Add />}
                                      onClick={() => navigate('/offers/new')}
                                      sx={{ borderRadius: '8px', textTransform: 'none', borderColor: BORDER }}>
                                İlk teklifini oluştur
                              </Button>
                          )}
                        </TableCell>
                      </TableRow>
                  ) : (
                      filtered.map(o => {
                        const isExpanded = expandedId === o.id;
                        const offerSum   = isExpanded ? calcOfferSummary(o) : null;
                        const isFL       = o.orderNo.includes('-FL-');

                        return (
                            <React.Fragment key={o.id}>
                              <TableRow style={{ background: isFL ? '#faf5ff' : 'transparent' }}
                                        sx={{
                                          transition: 'background .1s',
                                          '& td': { borderBottom: '1px solid #e8eaf0', py: 0 },
                                          '&:hover td': { bgcolor: '#fafbfe !important' },
                                        }}>
                                {/* Expand toggle */}
                                <TableCell sx={{ height: 56, width: 40, px: 0.5 }}>
                                  {isAdmin && (
                                      <IconButton size="small" onClick={() => setExpandedId(isExpanded ? null : o.id)}
                                                  sx={{ color: isExpanded ? BRAND : TEXT2, '&:hover': { color: BRAND } }}>
                                        {isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                                      </IconButton>
                                  )}
                                </TableCell>

                                {/* Teklif No */}
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    {isFL && (
                                        <Chip label="FL" size="small" sx={{
                                          bgcolor: '#7C3AED', color: '#fff',
                                          fontWeight: 700, fontSize: '10px', height: 18,
                                        }} />
                                    )}
                                    <Typography sx={{
                                      fontFamily: 'monospace', fontSize: '13px', fontWeight: 700,
                                      color: isFL ? '#7C3AED' : (isDark ? '#5b8fe8' : BRAND),
                                    }}>
                                      {o.orderNo}
                                    </Typography>
                                  </Box>
                                </TableCell>

                                {/* Müşteri */}
                                <TableCell>
                                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: theme.palette.text.primary }}>
                                    {o.customerName}
                                  </Typography>
                                  {o.customerCountry && (
                                      <Typography sx={{ fontSize: '11px', color: TEXT2 }}>{o.customerCountry}</Typography>
                                  )}
                                </TableCell>

                                {/* Hazırlayan (admin) */}
                                {isAdmin && (
                                    <TableCell>
                                      <Typography sx={{ fontSize: '12px', color: TEXT2 }}>{o.createdByName}</Typography>
                                    </TableCell>
                                )}

                                {/* Para birimi */}
                                <TableCell>
                                  <Chip label={o.currency} size="small" variant="outlined"
                                        sx={{ fontWeight: 700, fontSize: '11px', height: 20, borderRadius: '6px' }} />
                                </TableCell>

                                {/* Teslim */}
                                <TableCell>
                                  <Typography sx={{ fontSize: '12px', color: TEXT2 }}>{o.deliveryLocation}</Typography>
                                </TableCell>

                                {/* Durum */}
                                <TableCell>
                                  <StatusChip status={o.status} />
                                </TableCell>

                                {/* Tarih */}
                                <TableCell>
                                  <Typography sx={{ fontSize: '12px', color: TEXT2 }}>
                                    {new Date(o.createdAt).toLocaleDateString('tr-TR')}
                                  </Typography>
                                </TableCell>

                                {/* İşlemler */}
                                <TableCell align="center" sx={{ pr: 2 }}>
                                  <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                    <Tooltip title="Düzenle">
                                      <IconButton size="small" onClick={() => navigate(`/offers/${o.id}/edit`)}
                                                  style={{ width: 30, height: 30, borderRadius: 6, border: '1px solid #e8eaf0', background: '#fff', color: '#64748b' }}
                                                  sx={{ '&:hover': { background: '#fff8ed !important', color: '#d97706 !important', borderColor: '#fed7aa !important' } }}>
                                        <Edit sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Detay">
                                      <IconButton size="small" onClick={() => navigate(`/offers/${o.id}`)}
                                                  style={{ width: 30, height: 30, borderRadius: 6, border: '1px solid #e8eaf0', background: '#fff', color: '#64748b' }}
                                                  sx={{ '&:hover': { background: '#eff6ff !important', color: '#1d4ed8 !important', borderColor: '#c7d2fe !important' } }}>
                                        <Visibility sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                    {isAdmin && (
                                        <Tooltip title="Sil">
                                          <IconButton size="small" onClick={() => handleDelete(o.id)}
                                                      style={{ width: 30, height: 30, borderRadius: 6, border: '1px solid #e8eaf0', background: '#fff', color: '#64748b' }}
                                                      sx={{ '&:hover': { background: '#fff0f0 !important', color: '#dc2626 !important', borderColor: '#fca5a5 !important' } }}>
                                            <Delete sx={{ fontSize: 14 }} />
                                          </IconButton>
                                        </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>

                              {/* Expandable satır — teklif analizi */}
                              {isAdmin && (
                                  <TableRow>
                                    <TableCell colSpan={colSpan} sx={{ p: 0, border: 0 }}>
                                      <Collapse in={isExpanded} unmountOnExit>
                                        <Box style={{ padding: '20px 24px', background: '#f8faff', borderBottom: '2px solid #dbeafe' }}>
                                          {offerSum ? (() => {
                                            const sym = offerSum.currency === 'TRY' ? '₺' : offerSum.currency === 'EUR' ? '€' : '$';
                                            return (
                                                <>
                                                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                                                    <MetricCard label="Toplam Tutar"   val={`${sym}${fmt(offerSum.totalAmount)}`}                              bg="#e3f2fd" color="#1565c0" />
                                                    <MetricCard label="Hammadde"       val={`${sym}${fmt(offerSum.hammaddeToplam)}`}                           bg="#f3e5f5" color="#6a1b9a" />
                                                    <MetricCard label="Brüt Kâr"       val={`${sym}${fmt(offerSum.totalAmount - offerSum.hammaddeToplam)}`}    bg="#f0fdf4" color="#166534" />
                                                    <MetricCard label="Brüt Kârlılık"  val={`%${fmt(offerSum.brutKar, 1)}`}                                    bg="#e8f5e9" color="#2e7d32" />
                                                    <MetricCard label="Net Kârlılık"   val={`%${fmt(offerSum.netKar, 1)}`}                                     bg="#e8f5e9" color="#2e7d32" />
                                                    <MetricCard label="Ağırlık"        val={`${fmt(offerSum.totalKg, 0)} kg`}                                  bg="#fff8e1" color="#e65100" />
                                                  </Box>
                                                  <Divider sx={{ my: 1.5 }} />
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                                                    <Inventory2 sx={{ fontSize: 13, color: TEXT2 }} />
                                                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: TEXT2, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                                                      Hammadde Dağılımı
                                                    </Typography>
                                                  </Box>
                                                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                                    <MetricCard label="Alüminyum" val={`${sym}${fmt(offerSum.alCost)}`} bg={matColor['AL']} color="#e65100" />
                                                    {Object.entries(offerSum.hammedde)
                                                        .filter(([_, v]) => v.cost > 0.01)
                                                        .sort((a, b) => b[1].cost - a[1].cost)
                                                        .map(([key, val]) => (
                                                            <MetricCard key={key} label={matLabel[key] || key} val={`${sym}${fmt(val.cost)}`} bg={matColor[key] || '#f5f5f5'} color="#37474f" />
                                                        ))}
                                                  </Box>
                                                </>
                                            );
                                          })() : (
                                              <Typography sx={{ fontSize: '13px', color: TEXT2 }}>
                                                Bu teklif için detay bilgisi mevcut değil.
                                              </Typography>
                                          )}
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                              )}
                            </React.Fragment>
                        );
                      })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
        )}
      </Box>
  );
};

export default OffersPage;
